import { NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { Boxes, ChartNoAxesCombined, FileText, LayoutDashboard, LogOut, Map, MapPin, Network, Package, Settings, ShieldCheck, ShoppingBag, Store, Users, UserRoundCog } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import PermissionGate from "./PermissionGate";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Overview", permission: null, icon: LayoutDashboard },
  { to: "/admins", label: "Admin Management", permission: null, superAdminOnly: true, icon: ShieldCheck },
  { to: "/users", label: "Users", permission: "view_users", icon: Users },
  { to: "/orders", label: "Orders", permission: "view_orders", icon: ShoppingBag },
  { to: "/reports", label: "Reports", permission: "view_reports", icon: ChartNoAxesCombined },
  { to: "/content", label: "Content", permission: "manage_content", icon: FileText },
  { to: "/settings", label: "Settings", permission: "manage_settings", icon: Settings },
];

export default function AppShell({ children }) {
  const { admin, logout, isSuperAdmin, can } = useAuth();
  const location = useLocation();
  const [networkOpen, setNetworkOpen] = useState(location.pathname.startsWith("/network"));
  const [settingsOpen, setSettingsOpen] = useState(location.pathname.startsWith("/settings"));
  const [catalogOpen, setCatalogOpen] = useState(location.pathname.startsWith("/catalog"));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const networkItems = [
    { to: "/network/states", label: "States", permission: "manage_states", icon: Map },
    { to: "/network/districts", label: "Districts", permission: "manage_districts", icon: MapPin },
    { to: "/network/distributors", label: "Distributors", permission: "manage_distributors", icon: Store },
    { to: "/network/salespersons", label: "Sales Team", permission: "manage_salespersons", icon: UserRoundCog },
  ];
  const canSeeNetwork = networkItems.some((item) => can(item.permission));
  const networkActive = location.pathname.startsWith("/network");
  const settingsActive = location.pathname.startsWith("/settings");
  const catalogActive = location.pathname.startsWith("/catalog");

  const closeMobileNav = () => setMobileNavOpen(false);

  const overview = NAV_ITEMS[0];
  const remainingNavItems = NAV_ITEMS.slice(1);

  const renderNavLink = (item) => {
    if (item.superAdminOnly && !isSuperAdmin) return null;
    const Icon = item.icon;
    return (
      <NavLink
        key={item.to}
        to={item.to}
        className="sidebar-link"
        onClick={closeMobileNav}
        style={({ isActive }) => ({
          ...styles.navItem,
          ...(isActive ? styles.navItemActive : {}),
        })}
      >
        {Icon && <Icon className="nav-icon" size={17} strokeWidth={1.8} aria-hidden="true" />}
        <span>{item.label}</span>
      </NavLink>
    );
  };

  const renderSettings = () => (
    <div style={styles.networkGroup}>
      <button
        type="button"
        className={settingsActive ? "network-toggle network-toggle-active" : "network-toggle"}
        style={{ ...styles.navItem, ...styles.networkToggle, ...(settingsActive ? styles.navItemActive : {}) }}
        onClick={() => setSettingsOpen((open) => !open)}
        aria-expanded={settingsOpen}
        aria-controls="settings-navigation"
      >
        <span className="nav-group-label"><Settings className="nav-icon" size={17} strokeWidth={1.8} aria-hidden="true" />Settings</span><span style={{ ...styles.chevron, ...(settingsOpen ? styles.chevronOpen : {}) }} aria-hidden="true" />
      </button>
      {settingsOpen && (
        <div id="settings-navigation" style={styles.subNav}>
          <NavLink
            to="/settings/change-password"
            className="sidebar-sub-link"
            onClick={closeMobileNav}
            style={({ isActive }) => ({ ...styles.subNavItem, ...(isActive ? styles.navItemActive : {}) })}
          >
            <ShieldCheck className="nav-icon" size={15} strokeWidth={1.8} aria-hidden="true" /><span>Change password</span>
          </NavLink>
        </div>
      )}
    </div>
  );

  const renderCatalog = () => (
    <div style={styles.networkGroup}>
      <button
        type="button"
        className={catalogActive ? "network-toggle network-toggle-active" : "network-toggle"}
        style={{ ...styles.navItem, ...styles.networkToggle, ...(catalogActive ? styles.navItemActive : {}) }}
        onClick={() => setCatalogOpen((open) => !open)}
        aria-expanded={catalogOpen}
        aria-controls="catalog-navigation"
      >
        <span className="nav-group-label"><Package className="nav-icon" size={17} strokeWidth={1.8} aria-hidden="true" />Catalog</span><span style={{ ...styles.chevron, ...(catalogOpen ? styles.chevronOpen : {}) }} aria-hidden="true" />
      </button>
      {catalogOpen && <div id="catalog-navigation" style={styles.subNav}>
        {[{ to: "/catalog/categories", label: "Categories", icon: Boxes }, { to: "/catalog/subcategories", label: "Subcategories", icon: Network }, { to: "/catalog/products", label: "Products", icon: Package }].map((item) => { const Icon = item.icon; return <NavLink key={item.to} to={item.to} className="sidebar-sub-link" onClick={closeMobileNav} style={({ isActive }) => ({ ...styles.subNavItem, ...(isActive ? styles.navItemActive : {}) })}><Icon className="nav-icon" size={15} strokeWidth={1.8} aria-hidden="true" /><span>{item.label}</span></NavLink>; })}
      </div>}
    </div>
  );

  return (
    <div className="app-shell" style={styles.wrap}>
      <button
        type="button"
        className="mobile-menu-button"
        style={styles.mobileMenuButton}
        onClick={() => setMobileNavOpen(true)}
        aria-label="Open navigation"
        aria-expanded={mobileNavOpen}
      >
        <span style={styles.menuIcon} />
      </button>

      {mobileNavOpen && (
        <button
          type="button"
          className="mobile-nav-overlay"
          style={styles.mobileNavOverlay}
          onClick={closeMobileNav}
          aria-label="Close navigation"
        />
      )}

      <aside className={mobileNavOpen ? "app-rail app-rail-open" : "app-rail"} style={styles.rail}>
        <div style={styles.plate}>
          <span style={styles.dot} />
          <span style={styles.wordmark}>SHAHA Group</span>
        </div>

        <nav style={styles.nav}>
          {renderNavLink(overview)}
          {canSeeNetwork && (
            <div style={styles.networkGroup}>
              <button
                type="button"
                      className={networkActive ? "network-toggle network-toggle-active" : "network-toggle"}
                style={{ ...styles.navItem, ...styles.networkToggle, ...(networkActive ? styles.navItemActive : {}) }}
                onClick={() => setNetworkOpen((open) => !open)}
                aria-expanded={networkOpen}
                aria-controls="network-navigation"
              >
                <span className="nav-group-label"><Network className="nav-icon" size={17} strokeWidth={1.8} aria-hidden="true" />Network</span><span style={{ ...styles.chevron, ...(networkOpen ? styles.chevronOpen : {}) }} aria-hidden="true" />
              </button>
              {networkOpen && (
                <div id="network-navigation" style={styles.subNav}>
                  {networkItems.map((item) => can(item.permission) && (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className="sidebar-sub-link"
                      onClick={closeMobileNav}
                      style={({ isActive }) => ({ ...styles.subNavItem, ...(isActive ? styles.navItemActive : {}) })}
                    >
                      <item.icon className="nav-icon" size={15} strokeWidth={1.8} aria-hidden="true" />
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
          {can("view_products") || can("manage_products") ? renderCatalog() : null}
          {remainingNavItems.map((item) => item.to === "/settings" ? (
            <PermissionGate key={item.to} permission={item.permission}>
              {renderSettings()}
            </PermissionGate>
          ) : item.permission ? (
            <PermissionGate key={item.to} permission={item.permission}>
              {renderNavLink(item)}
            </PermissionGate>
          ) : renderNavLink(item))}
        </nav>

        <div style={styles.identity}>
          <div style={styles.identityRole}>{isSuperAdmin ? "Super Admin" : admin?.role === "salesperson" ? "Sales Person" : admin?.role === "distributor" ? "Distributor" : "Sub Admin"}</div>
          <div style={styles.identityName}>{admin?.name}</div>
          <div style={styles.identityEmail}>{admin?.email}</div>
          <button className="sidebar-logout" style={styles.logout} onClick={() => { logout(); closeMobileNav(); }}>
            <LogOut size={15} aria-hidden="true" /> Sign out
          </button>
        </div>
      </aside>

      <main style={styles.main}>{children}</main>
    </div>
  );
}

const styles = {
  wrap: { display: "flex", minHeight: "100vh", background: "var(--paper)" },
  rail: {
    width: 240,
    background: "var(--brand-gradient)",
    color: "var(--white)",
    display: "flex",
    flexDirection: "column",
    padding: "24px 16px",
    flexShrink: 0,
    zIndex: 20,
  },
  plate: { display: "flex", alignItems: "center", gap: 8, padding: "0 8px", marginBottom: 28 },
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "var(--signal)",
    boxShadow: "0 0 8px var(--signal)",
  },
  wordmark: { fontFamily: "var(--font-display)", fontSize: 19 },
  nav: { display: "flex", flexDirection: "column", gap: 2, flex: 1 },
  networkGroup: { display: "flex", flexDirection: "column" },
  navItem: {
    padding: "10px 12px",
    borderRadius: 4,
    color: "rgba(255, 255, 255, 0.82)",
    textDecoration: "none",
    fontSize: 14,
    borderLeft: "2px solid transparent",
  },
  navItemActive: {
    background: "rgba(255, 255, 255, 0.18)",
    color: "var(--white)",
    borderLeft: "2px solid var(--white)",
  },
  networkToggle: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    textAlign: "left",
    background: "transparent",
    fontFamily: "var(--font-ui)",
    borderTop: "none",
    borderRight: "none",
    borderBottom: "none",
    cursor: "pointer",
  },
  chevron: {
    width: 8,
    height: 8,
    borderRight: "1.5px solid currentColor",
    borderBottom: "1.5px solid currentColor",
    transform: "rotate(45deg) translateY(-2px)",
    transition: "transform 160ms ease",
  },
  chevronOpen: { transform: "rotate(225deg) translate(-1px, -1px)" },
  subNav: { display: "flex", flexDirection: "column", gap: 2, padding: "2px 0 6px 12px" },
  subNavItem: { padding: "8px 12px", borderRadius: 4, color: "rgba(255, 255, 255, 0.72)", textDecoration: "none", fontSize: 13, borderLeft: "2px solid transparent" },
  identity: {
    borderTop: "1px solid rgba(255, 255, 255, 0.28)",
    paddingTop: 16,
    marginTop: 16,
    padding: "16px 8px 0",
  },
  identityRole: {
    fontSize: 11,
    letterSpacing: 0.3,
    color: "var(--white)",
    fontWeight: 600,
  },
  identityName: { fontSize: 14, marginTop: 4 },
  identityEmail: { fontSize: 12, color: "rgba(255, 255, 255, 0.68)", marginBottom: 12 },
  logout: {
    width: "100%",
    background: "transparent",
    border: "1px solid rgba(255, 255, 255, 0.45)",
    color: "var(--white)",
    borderRadius: 4,
    padding: "8px 0",
    fontSize: 13,
  },
  main: { flex: 1, padding: "32px 40px", overflowY: "auto" },
  mobileMenuButton: {
    display: "none",
    position: "fixed",
    top: 16,
    left: 16,
    width: 42,
    height: 42,
    border: "1px solid #c7d5e8",
    borderRadius: 6,
    background: "var(--white)",
    zIndex: 10,
  },
  menuIcon: {
    display: "block",
    width: 18,
    height: 12,
    margin: "auto",
    borderTop: "2px solid var(--ink)",
    borderBottom: "2px solid var(--ink)",
    position: "relative",
  },
  mobileNavOverlay: {
    display: "none",
    position: "fixed",
    inset: 0,
    border: "none",
    background: "rgba(1, 18, 55, 0.42)",
    zIndex: 15,
  },
};
